import React, {Fragment, useEffect, useState} from 'react';
import ButtonSpinner from './ButtonSpinner';
import ConfirmationModal from './ConfirmationModal';
import ExternalModal from './ExternalModal';
import './UserAccessModal.scss';

const userAccessMeasurementTypes = [
    'TEMPERATURE',
    'HUMIDITY',
    'RAIN_COUNT',
    'WET_LEAF_COUNT',
    'WET_LEAF_MOISTURE',
    'SOIL_MOISTURE',
    'SOIL_TEMPERATURE',
    'SOIL_CONDUCTIVITY',
    'WIND_SPEED',
    'WIND_DIRECTION'
];

const userAccessColumns = [
    { key: 'hasAccess', label: 'Access' },
    { key: 'firstName', label: 'First name' },
    { key: 'lastName', label: 'Last name' },
    { key: 'email', label: 'e-mail' },
    { key: 'distance', label: 'Distance' }
];

const UserAccessModal = props => {
    const { t, itemId, idKey, getEndpoint, setEndpoint, closeMethod } = props;
    const [users, setUsers] = useState([]);
    const [savedUsers, setSavedUsers] = useState([]);
    const [measurementType, setMeasurementType] = useState('TEMPERATURE');
    const [pending, setPending] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(false);
    const [closeConfirmation, setCloseConfirmation] = useState(false);
    const [confirmationAction, setConfirmationAction] = useState({type: 'close', measurementType: ''});
    const [sortConfig, setSortConfig] = useState({key: 'distance', direction: 'asc'});

    const getAccessPayload = type => ({
        [idKey]: itemId,
        measurementType: type || measurementType
    });

    const getSelectedUserIds = userList => userList
        .filter(user => Boolean(user.hasAccess))
        .map(user => user.id);

    const normalizeAccess = userList => userList
        .map(user => ({
            ...user,
            hasAccess: Boolean(user.hasAccess)
        }));

    const usersAccessKey = userList => getSelectedUserIds(userList)
        .sort((a, b) => a - b)
        .join(',');

    const hasChanges = usersAccessKey(users) !== usersAccessKey(savedUsers);

    const formatDistance = distance => distance !== null && distance !== undefined
        ? `~ ${distance} km`
        : '-';

    const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, {sensitivity: 'base'});

    const compareUsers = (firstUser, secondUser) => {
        const { key, direction } = sortConfig;
        const directionMultiplier = direction === 'asc' ? 1 : -1;

        if (key === 'distance') {
            const firstDistance = firstUser.distance;
            const secondDistance = secondUser.distance;
            const firstIsNull = firstDistance === null || firstDistance === undefined;
            const secondIsNull = secondDistance === null || secondDistance === undefined;

            if (firstIsNull && secondIsNull) {
                return 0;
            }
            if (firstIsNull) {
                return 1;
            }
            if (secondIsNull) {
                return -1;
            }

            return (Number(firstDistance) - Number(secondDistance)) * directionMultiplier;
        }

        if (key === 'hasAccess') {
            return (Number(Boolean(firstUser.hasAccess)) - Number(Boolean(secondUser.hasAccess))) * directionMultiplier;
        }

        return compareText(firstUser[key], secondUser[key]) * directionMultiplier;
    };

    const sortedUsers = [...users].sort(compareUsers);

    const handleSort = key => {
        setSortConfig(sortConfig.key === key
            ? {
                key,
                direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
            }
            : {
                key,
                direction: 'asc'
            }
        );
    };

    const getSortIcon = key => {
        if (sortConfig.key !== key) {
            return 'unfold_more';
        }
        return sortConfig.direction === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
    };

    const fetchUsers = type => {
        setPending(true);
        setError(false);

        fetch(`${process.env.REACT_APP_API_URL}/${getEndpoint}`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json; charset=UTF-8',
                'Authorization': sessionStorage.getItem('agronetToken'),
                'Local': localStorage.getItem('lang') ? localStorage.getItem('lang') : 'en'
            },
            body: JSON.stringify(getAccessPayload(type))
        })
            .then(res => {
                if (res.status === 200) {
                    return res.json();
                }
                throw new Error(`Error: ${res.status} ${res.statusText}`);
            })
            .then(res => {
                const normalizedUsers = normalizeAccess(Array.isArray(res) ? res : []);
                setUsers(normalizedUsers);
                setSavedUsers(normalizedUsers);
            })
            .catch(err => {
                console.log('ERROR', err);
                setError(true);
            })
            .finally(() => {
                setPending(false);
            });
    };

    useEffect(() => {
        if (itemId) {
            fetchUsers(measurementType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId]);

    const handleMeasurementTypeChange = type => {
        if (hasChanges) {
            setConfirmationAction({type: 'measurementType', measurementType: type});
            setCloseConfirmation(true);
            return;
        }
        setMeasurementType(type);
        fetchUsers(type);
    };

    const handleUserAccessChange = userId => {
        setUsers(users.map(user => user.id === userId
            ? { ...user, hasAccess: !Boolean(user.hasAccess) }
            : user
        ));
    };

    const handleClose = () => {
        if (hasChanges) {
            setConfirmationAction({type: 'close', measurementType: ''});
            setCloseConfirmation(true);
            return;
        }
        closeMethod();
    };

    const discardChanges = () => {
        setCloseConfirmation(false);
        if (confirmationAction.type === 'measurementType') {
            setMeasurementType(confirmationAction.measurementType);
            fetchUsers(confirmationAction.measurementType);
            return;
        }
        closeMethod();
    };

    const handleSave = e => {
        e.preventDefault();
        setSaving(true);
        setError(false);

        fetch(`${process.env.REACT_APP_API_URL}/${setEndpoint}`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json; charset=UTF-8',
                'Authorization': sessionStorage.getItem('agronetToken'),
                'Local': localStorage.getItem('lang') ? localStorage.getItem('lang') : 'en'
            },
            body: JSON.stringify({
                ...getAccessPayload(),
                userIds: getSelectedUserIds(users)
            })
        })
            .then(res => {
                if (res.status === 200) {
                    return res.json();
                }
                throw new Error(`Error: ${res.status} ${res.statusText}`);
            })
            .then(res => {
                const normalizedUsers = normalizeAccess(Array.isArray(res) ? res : []);
                setUsers(normalizedUsers);
                setSavedUsers(normalizedUsers);
            })
            .catch(err => {
                console.log('ERROR', err);
                setError(true);
            })
            .finally(() => {
                setSaving(false);
            });
    };

    return (
        <Fragment>
            <ExternalModal
                closeMethod={handleClose}
                title={t.gettext('User selection')}
            >
                <div className='userAccessModal'>
                    <div className='userAccessToolbar'>
                        <label className='userAccessToolbarLabel'>
                            {t.gettext('Measurement type')}
                        </label>
                        <select
                            className='userAccessSelectInput'
                            value={measurementType}
                            onChange={e => handleMeasurementTypeChange(e.target.value)}
                            disabled={pending || saving}
                        >
                            {userAccessMeasurementTypes.map(type => (
                                <option key={type} value={type}>
                                    {t.gettext(type)}
                                </option>
                            ))}
                        </select>
                    </div>
                    {error && (
                        <div className='userAccessError'>
                            {t.gettext('Could not load users')}
                        </div>
                    )}
                    <div className='userAccessList'>
                        <div className='userAccessRow userAccessHeader'>
                            {userAccessColumns.map(column => (
                                <button
                                    key={column.key}
                                    type='button'
                                    className='userAccessSortButton'
                                    onClick={() => handleSort(column.key)}
                                >
                                    <span>{t.gettext(column.label)}</span>
                                    <span className='material-icons userAccessSortIcon'>
                                        {getSortIcon(column.key)}
                                    </span>
                                </button>
                            ))}
                        </div>
                        {pending
                            ? <div className='userAccessState'>{t.gettext('Loading')}</div>
                            : sortedUsers.map(user => (
                                <label
                                    key={user.id}
                                    className='userAccessRow userAccessUserRow'
                                >
                                    <div>
                                        <input
                                            type='checkbox'
                                            checked={Boolean(user.hasAccess)}
                                            onChange={() => handleUserAccessChange(user.id)}
                                            disabled={saving}
                                        />
                                    </div>
                                    <div>{user.firstName}</div>
                                    <div>{user.lastName}</div>
                                    <div>{user.email}</div>
                                    <div>{formatDistance(user.distance)}</div>
                                </label>
                            ))
                        }
                        {!pending && !users.length && (
                            <div className='userAccessState'>{t.gettext('No users')}</div>
                        )}
                    </div>
                    <div className='userAccessButtons'>
                        <button
                            className='userAccessCancelButton'
                            onClick={e => {
                                e.preventDefault();
                                handleClose();
                            }}
                            disabled={saving}
                        >
                            {t.gettext('Cancel')}
                        </button>
                        <button
                            className='userAccessSaveButton'
                            onClick={handleSave}
                            disabled={saving || pending || !hasChanges}
                        >
                            {t.gettext('Save')}
                            {saving && <ButtonSpinner black/>}
                        </button>
                    </div>
                </div>
            </ExternalModal>
            {closeConfirmation && (
                <ConfirmationModal
                    text={t.gettext('You have unsaved changes. Close without saving?')}
                    closeMethod={() => setCloseConfirmation(false)}
                    confirmMethod={discardChanges}
                    confirmButtonText={t.gettext('OK')}
                />
            )}
        </Fragment>
    );
};

export default UserAccessModal;
